import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Calendar, ZoomIn } from "lucide-react";
import { useChateauImages } from "@/hooks/useChateauImages";
import { gtag } from "@/utils/gtag";
import { LightboxGallery } from "@/components/enhanced/LightboxGallery";
import { OptimizedImage } from "@/components/OptimizedImage";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface ChateauCardProps {
  chateau: {
    id: string;
    nom: string;
    taille: string;
    capacite: string;
    prix: string;
    description: string;
    caracteristiques: string[];
    ageRecommande: string;
    imagePosition?: number;
    quantite?: number;
  };
  onReservation: (chateauId: string) => void;
  hasDatesSelected?: boolean;
  availableCount?: number;
}

export const ChateauCard = ({ chateau, onReservation, hasDatesSelected = true, availableCount }: ChateauCardProps) => {
  const { imageUrls, isLoading } = useChateauImages(chateau.id);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [showDateAlert, setShowDateAlert] = useState(false);

  // Use database images if available, otherwise fallback to placeholder
  const displayImages = imageUrls.length > 0 ? imageUrls : ['/placeholder.svg'];
  const currentImage = displayImages[currentImageIndex] || '/placeholder.svg';

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % displayImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + displayImages.length) % displayImages.length);
  };

  return (
    <Card className="overflow-hidden shadow-card hover:shadow-playful transition-all duration-500 hover:scale-105 bg-white flex flex-col h-full">
      
      {/* Image Carousel */}
      <div className="relative h-64 overflow-hidden bg-gradient-subtle">
        {isLoading ? (
          <div className="w-full h-full bg-muted animate-pulse flex items-center justify-center">
            <span className="text-muted-foreground">Chargement...</span>
          </div>
        ) : (
          <div className="relative w-full h-full">
            <img
              src={currentImage}
              alt={chateau.nom}
              className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
              style={{ objectPosition: `center ${chateau.imagePosition ?? 50}%` }}
              onError={(e) => {
                e.currentTarget.src = '/placeholder.svg';
              }}
            />
            <button
              onClick={() => setLightboxOpen(true)}
              className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors flex items-center justify-center group"
            >
              <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          </div>
        )}
        
        {/* Prix Badge */}
        <div className="absolute top-4 right-4">
          <Badge variant="secondary" className="bg-white/95 text-primary font-bold text-lg px-3 py-1">
            {chateau.prix}
          </Badge>
        </div>

        {/* Navigation arrows si plusieurs images */}
        {displayImages.length > 1 && !isLoading && (
          <>
            <button 
              onClick={prevImage}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Image précédente"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <button 
              onClick={nextImage}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Image suivante"
            >
              <ArrowRight className="w-4 h-4" />
            </button>
          </>
        )}

        {/* Indicateurs d'images */}
        {displayImages.length > 1 && !isLoading && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1">
            {displayImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                }`}
              />
            ))}
          </div>
        )}
      </div>

      <CardHeader>
        <div className="flex justify-between items-start">
          <CardTitle className="text-xl font-display font-bold text-primary">
            {chateau.nom}
          </CardTitle>
          <div className="flex gap-2 items-center ml-2">
            {hasDatesSelected && availableCount !== undefined && (chateau.quantite ?? 1) > 1 && (
              <Badge variant={availableCount <= 1 ? "destructive" : "default"} className="text-xs whitespace-nowrap">
                {availableCount}/{chateau.quantite} dispo
              </Badge>
            )}
            {!hasDatesSelected && (chateau.quantite ?? 1) > 1 && (
              <Badge variant="secondary" className="text-xs whitespace-nowrap">
                Stock: {chateau.quantite}
              </Badge>
            )}
            <Badge variant="outline">{chateau.ageRecommande}</Badge>
          </div>
        </div>
        <p className="text-muted-foreground">{chateau.description}</p>
      </CardHeader>

      <CardContent className="space-y-4 flex-1 flex flex-col">
        <div className="grid grid-cols-1 xs:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-semibold">Taille:</span>
            <p className="text-muted-foreground">{chateau.taille}</p>
          </div>
          <div>
            <span className="font-semibold">Capacité:</span>
            <p className="text-muted-foreground">{chateau.capacite}</p>
          </div>
        </div>

        <div className="flex-1">
          <span className="font-semibold text-sm">Caractéristiques:</span>
          <div className="flex flex-wrap gap-1 mt-1">
            {chateau.caracteristiques.map((caracteristique, index) => (
              <Badge key={index} variant="secondary" className="text-xs">
                {caracteristique}
              </Badge>
            ))}
          </div>
        </div>

        <div className="flex flex-col xs:flex-row gap-3 mt-auto">
          <Button 
            className="flex-1 min-h-[48px] text-base"
            onClick={() => {
              if (!hasDatesSelected) {
                setShowDateAlert(true);
                return;
              }
              // Track valid conversion for Google Ads
              gtag('event', 'conversion', {
                send_to: 'AW-1049756763/W1ONCJSag-kZEIOLqLMq',
                value: parseInt(chateau.prix) || 60,
                currency: 'EUR',
                event_category: 'chateau',
                event_label: chateau.nom
              });
              
              // Track add to cart / begin checkout
              gtag('event', 'begin_checkout', {
                currency: 'EUR',
                value: parseInt(chateau.prix) || 60,
                items: [{
                  item_id: chateau.id,
                  item_name: chateau.nom,
                  item_category: 'Château Gonflable',
                  price: parseInt(chateau.prix) || 60,
                  quantity: 1
                }]
              });
              
              onReservation(chateau.id);
            }}
          >
            <Calendar className="w-4 h-4 mr-2" />
            <span className="xs:hidden">Réserver</span>
            <span className="hidden xs:inline">Demande de réservation</span>
          </Button>
          <Button 
            variant="outline"
            className="flex-1 min-h-[48px] text-base"
            onClick={() => {
              // Track product detail view
              gtag('event', 'view_item', {
                currency: 'EUR',
                value: parseInt(chateau.prix) || 60,
                items: [{
                  item_id: chateau.id,
                  item_name: chateau.nom,
                  item_category: 'Château Gonflable',
                  price: parseInt(chateau.prix) || 60,
                  quantity: 1
                }]
              });
            }}
            asChild
          >
            <a href={`/chateau/${chateau.id}`} className="flex items-center justify-center">
              Voir plus
            </a>
          </Button>
        </div>
      </CardContent>
      
      <LightboxGallery
        images={displayImages}
        initialIndex={currentImageIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        title={chateau.nom}
        description={chateau.description}
        onImageChange={setCurrentImageIndex}
      />

      <AlertDialog open={showDateAlert} onOpenChange={setShowDateAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>📅 Dates requises</AlertDialogTitle>
            <AlertDialogDescription>
              Pour faire une demande de réservation, veuillez d'abord sélectionner vos dates d'événement en haut de la page. Cela nous permet de vérifier la disponibilité du château.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => {
              setShowDateAlert(false);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}>
              Choisir mes dates
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};